import { isOnlineNow } from './connectivity';
import {
  isRefreshUnavailableError,
  isSessionChangedError,
  isSessionEndedError,
} from '../gql/refreshFailure';

/**
 * Clasificación de fallos de transporte, compartida por el `OfflineQueueLink`
 * (que la usa sobre el error crudo del link) y por el transporte de fase
 * (`phase2Transport`, que la usa sobre el `networkError` del ApolloError). Vivía
 * dentro del link; se extrae sin cambiar una línea para que las dos
 * clasificaciones no diverjan (spec 2026-09-08-fase2-encolada § 3.1).
 */

const GATEWAY_DOWN_CODES = new Set([502, 503, 504]);

/**
 * ¿Fallo de transporte? — la regla de siempre: sin red, sin `statusCode`
 * (`networkError` puro), o el texto de `fetch` (`Failed to fetch` en Chromium
 * y en el WebView del APK, `NetworkError` en Firefox).
 */
export function isLegacyNetworkFailure(error: any): boolean {
  // 🔴 Una sesión terminada nunca es transporte, ni aunque A LA VEZ se caiga la
  // red: encolar esa escritura la dejaría SIN dueño (la sesión ya está
  // borrada) y la enviaría quien entre después. Va antes de `!isOnlineNow()`.
  // Lo heredan isDegraded e isTransportFailure.
  if (isSessionEndedError(error)) return false;
  // Un cambio de sesión tampoco: la operación era de otra sesión (spec § 12.1).
  if (isSessionChangedError(error)) return false;
  // El refresco del token no se pudo hacer (red, plazo, 5xx): para la escritura
  // es un fallo de transporte y se encola, como cuando el error traía
  // `networkError` (spec 2026-10-01 § 5.2). Lo heredan isDegraded e
  // isTransportFailure.
  if (isRefreshUnavailableError(error)) return true;
  const hard = error?.networkError && !error.networkError.statusCode;
  const text =
    typeof error?.message === 'string' &&
    (error.message.includes('Failed to fetch') ||
      error.message.includes('NetworkError'));
  return !isOnlineNow() || !!hard || text;
}

/**
 * Ampliación SOLO para la allowlist: proxy caído (502/503/504 de HAProxy),
 * respuesta truncada (`ServerParseError`), abort y cualquier `TypeError` de
 * transporte. Nunca un error con `graphQLErrors` ni un 4xx de aplicación: eso
 * es respuesta del servidor, no red.
 */
export function isDegradedNetworkFailure(error: any): boolean {
  if (isLegacyNetworkFailure(error)) return true;
  if (Array.isArray(error?.graphQLErrors) && error.graphQLErrors.length > 0) {
    return false;
  }
  if (typeof error?.statusCode === 'number') {
    return (
      GATEWAY_DOWN_CODES.has(error.statusCode) ||
      error.name === 'ServerParseError'
    );
  }
  return (
    error?.name === 'TypeError' ||
    error?.name === 'AbortError' ||
    error?.name === 'ServerParseError'
  );
}
